import { I18n } from "@assets/strings";
import React from "react";
import { Link } from "react-router";
import styled, { css } from "styled-components";
import { Path } from "@core/routing/path";
import { useServices } from "@core/servicesContext";
import { useCurrentProfile } from "@domain/currentProfile/hooks";
import { Course } from "@domain/lms/course/course";
import { getDisplayedAuthor } from "@domain/lms/utils";
import { useIsMobile } from "@utils/hooks";
import { PrimaryButton } from "@ui/components/buttons/primaryButton";
import { SecondaryButton } from "@ui/components/buttons/secondaryButton";
import { PrimaryMedium } from "@ui/components/containers/primary";
import { EditIcon, RepostIcon } from "@ui/components/icons";
import { LinkExternal } from "@ui/components/linkExternal";
import { EditReferencePostModal } from "@ui/components/post/reference/editReferencePostModal";
import { Toolbar } from "@ui/components/toolbar";
import { useModalTools } from "@ui/modals/modalContext";
import { gap, mobile } from "@ui/styles/utils";
import { Z } from "@ui/styles/zIndex";
import { AuthorView } from "./authorView";
import { ChaptersSummary } from "@ui/pages/lms/coursePage/contentSummary/chaptersSummary";
import { DetailedCourseHeader } from "./detailedCourseHeader";
import { CourseFeedbacks } from "@ui/pages/lms/coursePage/feedback/courseFeedbacks";

interface CoursePresentationProps extends React.HTMLAttributes<HTMLDivElement> {
	course: Course;
}
export const CoursePresentation: React.FC<CoursePresentationProps> = ({ course, ...htmlProps }) => {
	const { courseService, trackingService } = useServices();
	const { id, reference } = course;
	const { createModal } = useModalTools();
	const currentProfile = useCurrentProfile();
	const isAuthor = course.author?.id === currentProfile?.id;
	const isMobile = useIsMobile();

	return (
		<Container {...htmlProps}>
			<CourseContent>
				<DetailedCourseHeader course={course} />
				<ChaptersSummary courseId={id} chapters={course.content.chapters} />
				{course.provider === "TOOSHARE" && <CourseFeedbacks course={course} />}
			</CourseContent>
			<AuthorWrapper>
				<PrimaryMedium>{I18n.t("course.author.label")}</PrimaryMedium>
				<AuthorView author={getDisplayedAuthor(course)} />
			</AuthorWrapper>
			{currentProfile && (
				<StyledToolbar>
					{isAuthor && (
						<Link to={Path.Lms.Editor(course.id)}>
							<StyledSecondaryButton>
								<EditIcon />
								{!isMobile && I18n.t("edit")}
							</StyledSecondaryButton>
						</Link>
					)}
					<StyledSecondaryButton
						disabled={course.status === "DRAFT"}
						onClick={
							course.status === "DRAFT"
								? undefined
								: () =>
										createModal((close) => (
											<EditReferencePostModal close={close} shared={{ type: "SHARE_COURSE", reference: course }} />
										))
						}
						$disabled={course.status === "DRAFT"}
					>
						<RepostIcon /> {!isMobile && I18n.t("course.toolbar.share")}
					</StyledSecondaryButton>
					<LinkExternal to={reference ?? Path.Lms.CourseContent(id)}>
						<PrimaryButton
							onClick={() => {
								trackingService.track("course / start viewing", {
									provider: course.provider,
									language: course.language,
									duration: course.duration,
									category: course.category,
									subcategory: course.subCategory,
								});
								courseService.view(id);
							}}
						>
							{reference ? I18n.t(`course.toolbar.external.${course.provider}`) : I18n.t("course.toolbar.start")}
						</PrimaryButton>
					</LinkExternal>
				</StyledToolbar>
			)}
		</Container>
	);
};

const Container = styled.div`
	width: 100%;

	display: grid;
	grid-template-columns: 280px minmax(0, 1fr);
	grid-template-rows: auto;
	grid-column-gap: 20px;
	grid-row-gap: 24px;
	grid-template-areas:
		"author course"
		". course"
		". course"
		". course";

	@media (max-width: 900px) {
		grid-template-columns: 100%;

		grid-template-areas:
			"course"
			"author";
	}

	> a {
		align-self: flex-end;
		margin-bottom: 10px;
	}
	margin-bottom: 40px;
`;

const AuthorWrapper = styled.div`
	grid-area: author;
	position: sticky;
	top: 120px;
	${gap(15)};
`;

const CourseContent = styled.div`
	grid-area: course;
	${gap(40)};

	@media (max-width: 900px) {
		${gap(15)};
	}
`;

const StyledToolbar = styled(Toolbar)`
	${gap(26, "row")}
	justify-content: flex-end;
	z-index: ${Z.over};

	@media (max-width: 900px) {
		justify-content: center;
	}

	a button {
		height: 100%;
	}
`;

const StyledSecondaryButton = styled(SecondaryButton)<{ $disabled?: boolean }>`
	${({ $disabled }) =>
		$disabled &&
		css`
			svg {
				path {
					fill: ${({ theme }) => theme.colors.lightergray};
				}
			}
		`}
	@media ${mobile} {
		padding: 12px;
		border-radius: 24px;
		> svg {
			margin: 0;
		}
	}
`;
